import { FluzElementsError } from "./errors.js";
import { resolveFrameHostOrigin } from "./frameHostOrigin.js";
import { mountFrame } from "./frameMount.js";
import type { FrameMount } from "./frameMount.js";
import type { StyleAllowlist } from "./style.js";

export interface IdentityInputOptions {
  clientToken: string;
  loadToken: string;
  frameHostOrigin?: string;
  mountId?: string;
  style?: StyleAllowlist;
  mountTimeoutMs?: number;
  submitTimeoutMs?: number;
}

export interface IdentityFieldState {
  isEmpty: boolean;
  isValid: boolean;
  isDirty: boolean;
}

// Its own type, not card's BillingAddress: `state` is required here (TGS's
// verifyUserInformation declares it non-nullable; card's billing address has
// no such requirement) and there is no userAddressId shortcut -- TGS's
// prefill mutation takes no SSN/name/DOB, so routing a submit through it
// would collect the user's SSN digits and discard them unused. If prefill is wanted,
// it does not need a secure SSN frame at all.
export interface IdentityAddress {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export interface IdentitySubmitOptions {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  address: IdentityAddress;
}

// build plan §3.3: the success payload is the verification outcome only --
// APPROVED/DECLINED/DUPLICATE are all delivered here, not split across
// onSuccess/onDeclined the way card tokenization is, since there's no
// separate "the request itself failed" case to distinguish from a decline.
// No token, no alias, no SSN digits ever reach this payload.
export type IdentityVerificationStatus = "APPROVED" | "DECLINED" | "DUPLICATE" | "ERROR";

export interface IdentityVerificationResult {
  status: IdentityVerificationStatus;
  code?: string;
  message?: string;
}

export interface IdentityInputController {
  mount(container: HTMLElement): Promise<void>;
  onChange(handler: (state: IdentityFieldState) => void): () => void;
  onError(handler: (error: FluzElementsError) => void): () => void;
  onSuccess(handler: (result: IdentityVerificationResult) => void): () => void;
  submit(options: IdentitySubmitOptions): Promise<void>;
  destroy(): void;
}

const DEFAULT_SUBMIT_TIMEOUT_MS = 15000;

// Internal sentinel destroy() settles a pending submit() with; never emitted.
const DESTROYED_CODE = "destroyed";

function generateId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// Mirrors secureInputs.ts's SubmitAck -- the frame's one submit-result
// message carries the full outcome directly (see apps/frame-host/src/frame/
// renderIdentityFrameDocument.ts), never a network call of its own.
interface SubmitAck {
  ok: boolean;
  code?: string;
  result?: IdentityVerificationResult;
}

export function renderIdentityFieldsForTokenization(options: IdentityInputOptions): IdentityInputController {
  const frameHostOrigin = resolveFrameHostOrigin(options.frameHostOrigin);
  const mountId = options.mountId ?? generateId();

  const changeHandlers = new Set<(state: IdentityFieldState) => void>();
  const errorHandlers = new Set<(error: FluzElementsError) => void>();
  const successHandlers = new Set<(result: IdentityVerificationResult) => void>();
  let mount: FrameMount | null = null;
  let pendingSubmitResolver: ((ack: SubmitAck) => void) | null = null;
  let mounted = false;
  let submitInFlight = false;

  function emitError(error: FluzElementsError) {
    errorHandlers.forEach((handler) => handler(error));
  }

  return {
    async mount(container: HTMLElement) {
      if (mounted) {
        throw new FluzElementsError("MOUNT_FAILED", "IdentityInput is already mounted.");
      }
      mounted = true;

      const url = new URL("/v1/frame/ssn", frameHostOrigin);
      // Same split as secureInputs.ts's mount(): the short-lived,
      // load-only loadToken travels in the query string; the
      // collect-capable clientToken is only ever handed over via the
      // handshake postMessage below.
      url.searchParams.set("token", options.loadToken);

      try {
        mount = await mountFrame({
          mountId,
          frameUrl: url.toString(),
          frameOrigin: frameHostOrigin,
          container,
          style: options.style,
          mountTimeoutMs: options.mountTimeoutMs,
          onError: emitError,
          handshakeExtra: {
            clientToken: options.clientToken,
          },
          onMessage: (message) => {
            if (message.type === "field-change") {
              const payload = message.payload as { state: IdentityFieldState };
              changeHandlers.forEach((handler) => handler(payload.state));
              return;
            }
            if (message.type === "submit-result") {
              pendingSubmitResolver?.(message.payload as SubmitAck);
            }
          },
        });
      } catch (error) {
        mounted = false;
        throw error instanceof FluzElementsError ? error : new FluzElementsError("MOUNT_FAILED", "Failed to mount identity input.");
      }
    },

    onChange(handler) {
      changeHandlers.add(handler);
      return () => changeHandlers.delete(handler);
    },

    onError(handler) {
      errorHandlers.add(handler);
      return () => errorHandlers.delete(handler);
    },

    onSuccess(handler) {
      successHandlers.add(handler);
      return () => successHandlers.delete(handler);
    },

    async submit(submitOptions: IdentitySubmitOptions) {
      if (!mounted || !mount) {
        throw new FluzElementsError("MOUNT_FAILED", "IdentityInput is not mounted.");
      }
      if (submitInFlight) {
        throw new FluzElementsError("SUBMIT_FAILED", "A submit() call is already in progress.");
      }
      submitInFlight = true;

      try {
        const timeoutMs = options.submitTimeoutMs ?? DEFAULT_SUBMIT_TIMEOUT_MS;
        const ackPromise = new Promise<SubmitAck>((resolve) => {
          pendingSubmitResolver = resolve;
        });
        mount.send("submit", submitOptions);

        const ackTimeout: { id?: ReturnType<typeof setTimeout> } = {};
        const ack = await Promise.race([
          ackPromise,
          new Promise<"timeout">((resolve) => {
            ackTimeout.id = setTimeout(() => resolve("timeout"), timeoutMs);
          }),
        ]);
        clearTimeout(ackTimeout.id);
        pendingSubmitResolver = null;

        // destroy() settled this submit: the controller is gone, so there is
        // no one left to report an outcome to -- no stale error or success.
        if (ack !== "timeout" && ack.code === DESTROYED_CODE) {
          return;
        }

        if (ack === "timeout") {
          emitError(new FluzElementsError("SUBMIT_TIMEOUT", "Timed out waiting for a submit result."));
          return;
        }

        if (!ack.ok || !ack.result) {
          emitError(new FluzElementsError("SUBMIT_FAILED", ack.code ?? "submit_failed"));
          return;
        }

        successHandlers.forEach((handler) => handler(ack.result as IdentityVerificationResult));
      } finally {
        submitInFlight = false;
      }
    },

    destroy() {
      // Settle a pending submit() now instead of leaving it to its timeout:
      // otherwise submitInFlight stays set, so a re-mount couldn't submit
      // until the stale timeout fired, and that timeout would then report
      // SUBMIT_TIMEOUT for a submit the caller already tore down.
      pendingSubmitResolver?.({ ok: false, code: DESTROYED_CODE });
      pendingSubmitResolver = null;
      submitInFlight = false;
      mount?.destroy();
      mount = null;
      mounted = false;
    },
  };
}
